import React, { useMemo, useRef, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { LabExam } from '../../LabExam';

interface BarChartLabExamProps {
  data: LabExam[];
  filteredYears?: string[];
  onBarClick?: (year: string) => void;
  hideBottomAxis?: boolean;
  compact?: boolean;
}

const getYear = (dateStr: string) => {
  if (!dateStr) return '';
  const raw = String(dateStr).trim();

  // ISO-like: 2024-09-30 or 2024-09-30T12:00:00
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? '' : String(d.getFullYear());
  }

  // RU format: 30.09.2024
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(raw)) {
    const [dd, mm, yyyy] = raw.split('.').map(Number);
    if (yyyy && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) return String(yyyy);
  }

  // US-like: 09/30/2024
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const parts = raw.split('/').map(Number);
    const yyyy = parts[2];
    if (yyyy) return String(yyyy);
  }

  // Timestamp number
  if (/^\d{10,13}$/.test(raw)) {
    const ts = raw.length === 10 ? Number(raw) * 1000 : Number(raw);
    const d = new Date(ts);
    return isNaN(d.getTime()) ? '' : String(d.getFullYear());
  }

  // Fallback to Date parser
  const d = new Date(raw);
  return isNaN(d.getTime()) ? '' : String(d.getFullYear());
};

const STACKING_ORDER = [
  "Соответствует НД",
  "Не соответствует НД (фальсификат)",
  "Не соответствует НД (брак)",
  "тест",
  "Нет данных"
];

const EXAM_RESULT_COLORS = {
  resultColors: {
    'Соответствует НД': 'rgba(126, 178, 70, 1)',
    'Не соответствует НД (фальсификат)': 'rgba(237, 93, 98, 1)',
    'Не соответствует НД (брак)': 'rgba(255, 166, 0, 1)',
    'Нет данных': 'rgba(196, 196, 196, 1)',
  } as Record<string, string>,
  base: [
    'rgba(126, 178, 70, 1)',
    'rgba(237, 93, 98, 1)',
    'rgba(255, 166, 0, 1)',
    'rgba(196, 196, 196, 1)',
  ],
  dimmed: [
    'rgba(126, 178, 70, 0.2)',
    'rgba(237, 93, 98, 0.2)',
    'rgba(255, 166, 0, 0.2)',
    'rgba(196, 196, 196, 0.2)',
  ]
};

const tooltipWidth = 320;
const tooltipHeight = 180;

const BarChartLabExam: React.FC<BarChartLabExamProps> = ({ data, filteredYears, onBarClick, hideBottomAxis, compact = false }) => {
  const years = useMemo(() => {
    const set = new Set<string>();
    data.forEach(item => {
      const year = getYear(item.exam_date);
      if (year) set.add(year);
    });
    return Array.from(set).sort();
  }, [data]);

  const allExamResults = useMemo(() => {
    const set = new Set<string>();
    data.forEach(item => {
      if (item.exam_result) set.add(item.exam_result);
    });
    return Array.from(set).sort();
  }, [data]);

  const examResults = useMemo(() => {
    const set = new Set<string>();
    data.forEach(item => {
      if (item.exam_result) set.add(item.exam_result);
    });
    const sorted = Array.from(set).sort((a, b) => {
      const indexA = STACKING_ORDER.indexOf(a);
      const indexB = STACKING_ORDER.indexOf(b);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
    return sorted;
  }, [data]);

  const barData = useMemo(() => {
    return years.map(year => {
      const row: Record<string, any> = { year };
      examResults.forEach(result => {
        row[result] = data.filter(item => getYear(item.exam_date) === year && item.exam_result === result).length;
      });
      return row;
    });
  }, [years, examResults, data]);

  const getIntegerTickValues = (maxValue: number): number[] => {
    if (!isFinite(maxValue) || maxValue <= 0) return [0];
    const niceStep = (n: number) => {
      const pow = Math.pow(10, Math.floor(Math.log10(n)));
      const d = n / pow;
      let step: number;
      if (d <= 1) step = 1;
      else if (d <= 2) step = 2;
      else if (d <= 5) step = 5;
      else step = 10;
      return step * pow;
    };
    const roughStep = Math.ceil(maxValue / 8);
    const step = Math.max(1, niceStep(roughStep));
    const upper = Math.ceil(maxValue / step) * step;
    const ticks: number[] = [];
    for (let v = 0; v <= upper; v += step) ticks.push(v);
    return ticks;
  };

  const getBackgroundColors = () => {
    return examResults.map((result, idx) => {
      const specificColor = EXAM_RESULT_COLORS.resultColors[result];
      return specificColor || EXAM_RESULT_COLORS.base[idx % EXAM_RESULT_COLORS.base.length];
    });
  };

  const [barTooltip, setBarTooltip] = useState<null | { x: number; y: number; id: string; year: string }> (null);
  const barChartRef = useRef<HTMLDivElement>(null);

  const getBarInfo = (id: string, year: string) => {
    const row = barData.find((d: any) => d.year === year);
    if (!row) return null;
    const value = row[id] || 0;
    const total = examResults.reduce((sum, key) => sum + (row[key] || 0), 0);
    const percent = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
    return { value, total, percent, row };
  };

  function handleBarChartMouseMove(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!barChartRef.current) return;
    const svg = barChartRef.current.querySelector('svg');
    if (!svg) return;
    const bars = Array.from(svg.querySelectorAll('rect[data-id]'));
    for (const rect of bars) {
      const bbox = rect.getBoundingClientRect();
      if (
        e.clientX >= bbox.left &&
        e.clientX <= bbox.right &&
        e.clientY >= bbox.top &&
        e.clientY <= bbox.bottom
      ) {
        const dataId = rect.getAttribute('data-id');
        const parentGroup = rect.parentElement?.parentElement;
        let year = '';
        if (parentGroup) {
          const texts = parentGroup.querySelectorAll('text');
          texts.forEach(t => {
            if (/^\d{4}$/.test(t.textContent || '')) year = t.textContent || '';
          });
        }
        if (dataId && year) {
          setBarTooltip({
            x: e.nativeEvent.offsetX,
            y: e.nativeEvent.offsetY,
            id: dataId,
            year
          });
          return;
        }
      }
    }
    setBarTooltip(null);
  }
  function handleBarChartMouseLeave() {
    setBarTooltip(null);
  }

  let tooltipX = 0, tooltipY = 0, tooltipContent = null;
  if (barTooltip && barTooltip.id && barTooltip.year) {
    const info = getBarInfo(barTooltip.id, barTooltip.year);
    if (info) {
      const { value, total, percent, row } = info;
      const resultIndex = examResults.indexOf(barTooltip.id);
      const colors = getBackgroundColors();
      const color = colors[resultIndex] || EXAM_RESULT_COLORS.base[0];
      tooltipX = barTooltip.x + 18;
      tooltipY = barTooltip.y + 10;
      if (barChartRef.current) {
        const rect = barChartRef.current.getBoundingClientRect();
        if (tooltipX + tooltipWidth > rect.width) tooltipX = barTooltip.x - tooltipWidth - 18;
        if (tooltipX < 0) tooltipX = 0;
        if (tooltipY + tooltipHeight > rect.height) tooltipY = barTooltip.y - tooltipHeight - 10;
        if (tooltipY < 0) tooltipY = 0;
      }
      tooltipContent = (
        <div
          style={{
            position: 'absolute',
            left: tooltipX,
            top: tooltipY,
            zIndex: 1000,
            pointerEvents: 'none',
            padding: 16,
            background: '#fff',
            border: '1.5px solid #eee',
            borderRadius: 10,
            minWidth: tooltipWidth,
            maxWidth: tooltipWidth,
            boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
            fontSize: 15,
            color: '#222',
            fontFamily: 'inherit',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontWeight: 700, color, fontSize: 20 }}>{barTooltip.id}</span>
            <span style={{ fontSize: 16, color: '#888' }}>Год: <b>{barTooltip.year}</b></span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, marginBottom: 2 }}>
            <span>Количество:</span>
            <b style={{ fontWeight: 700, fontSize: 18 }}>{value}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, marginBottom: 8 }}>
            <span>Процент:</span>
            <b style={{ color, fontWeight: 700, fontSize: 18 }}>{percent}%</b>
          </div>
          <div style={{ borderTop: '1px solid #eee', margin: '8px 0 8px 0' }} />
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Всего за год: {total}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 15 }}>
            {examResults.map((key, idx) => {
              const v = row[key] || 0;
              const p = total > 0 ? ((v / total) * 100).toFixed(1) : '0.0';
              const colors = getBackgroundColors();
              const color = colors[idx] || EXAM_RESULT_COLORS.base[0];
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 1 }}>
                  <span style={{ color: color, fontWeight: 600, minWidth: 120 }}>{key}</span>
                  <span style={{ fontWeight: 500 }}>{v} ({p}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }} ref={barChartRef} onMouseMove={handleBarChartMouseMove} onMouseLeave={handleBarChartMouseLeave}>
      <ResponsiveBar
        data={barData}
        keys={examResults}
        indexBy="year"
        margin={compact ? { top: 20, right: 24, bottom: 72, left: 56 } : { top: 40, right: 40, bottom: 60, left: 60 }}
        padding={0.3}
        groupMode="stacked"
        colors={({ id, data }) => {
          const resultId = String(id);
          const resultIndex = examResults.indexOf(resultId);
          const bgColors = getBackgroundColors();
          const baseColor = bgColors[resultIndex] || EXAM_RESULT_COLORS.base[0];
          if (filteredYears && filteredYears.length > 0) {
            return filteredYears.includes(data.year)
              ? baseColor
              : (baseColor.includes('rgba') ? baseColor.replace('1)', '0.2)') : baseColor);
          }
          return baseColor;
        }}
        borderRadius={4}
        enableLabel={true}
        labelSkipWidth={16}
        labelSkipHeight={16}
        labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
        axisBottom={hideBottomAxis ? null : {
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'Год',
          legendPosition: 'middle',
          legendOffset: compact ? 44 : 40,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'Количество испытаний',
          legendPosition: 'middle',
          legendOffset: compact ? -42 : -50,
          tickValues: (() => {
            const max = barData.reduce((m, row) => {
              const total = examResults.reduce((s, k) => s + (row[k] || 0), 0);
              return Math.max(m, total);
            }, 0);
            return getIntegerTickValues(max);
          })()
        }}
        theme={{
          axis: {
            legend: { text: { fontSize: 16, fontWeight: 600 } },
            ticks: { text: { fontSize: 12, fontWeight: 600 } },
          },
        }}
        tooltip={({ id, value, color, indexValue, data }) => {
          const total = examResults.reduce((sum, key) => sum + Number(data[key] || 0), 0);
          const percent = total > 0 ? ((Number(value) / total) * 100).toFixed(1) : '0.0';
          return (
            <div
              style={{
                padding: 12,
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: 6,
                minWidth: 260,
                maxWidth: 340,
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <strong style={{ color }}>{String(id)}</strong>
                <span style={{ fontSize: 13, color: '#888' }}>Год: <b>{String(indexValue)}</b></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>Количество:</span>
                <b>{String(value)}</b>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>Процент:</span>
                <b>{percent}%</b>
              </div>
              <div style={{ borderTop: '1px solid #eee', margin: '4px 0' }} />
              <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 2 }}>Всего за год: {total}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, fontSize: 12 }}>
                {examResults.map((key, idx) => {
                  const v = Number(data[key] || 0);
                  const p = total > 0 ? ((v / total) * 100).toFixed(1) : '0.0';
                  const colors = getBackgroundColors();
                  const color = colors[idx] || EXAM_RESULT_COLORS.base[0];
                  return (
                    <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: 2 }}>
                      <span style={{ color: color, fontWeight: id === key ? 700 : 400 }}>{key}</span>
                      <span>{v} ({p}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }}
        legends={[]}
        animate={true}
        motionConfig="gentle"
        onClick={bar => onBarClick && onBarClick(bar.data.year)}
      />
      {tooltipContent}
    </div>
  );
};

export default BarChartLabExam; 
